import {
  createContext, useCallback, useContext, useEffect, useId, useRef, useState,
  type CSSProperties, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { T, useT } from "../i18n";
import { Knopf, Meldung } from "./index";
import { useSchliessanfrage } from "../schale/schliessanfrage";
import { useTelefon } from "../schale/geraet";
import { tastaturSchliessen } from "../schale/tastatur";
import { fokusZurueck, useBlattform, useZiehen } from "./Blattvonunten";

/* ══════════════════════════════════════════════════════════════════
   Das Deckblatt — ein Blatt, das sich über den Arbeitsbereich legt.

   **Warum es das gibt.** Wer aus der Schnellerfassung heraus einen Kunden
   anlegen wollte, sprang bis zum 15.09.2026 nach `/kunden/neu` — und
   alles, was bis dahin eingetippt war (Gerät, Zeitraum, Einsatzort), war
   weg. Am Telefon ist das der Punkt, an dem man auflegt. Das Deckblatt
   legt sich **über** die Maske, statt sie zu ersetzen: Was darunter
   steht, bleibt stehen, und nach dem Schließen ist es noch da.

   **Es füllt den Arbeitsbereich, nicht den Bildschirm.** Die Seitenleiste
   bleibt sichtbar, damit man weiß, wo man ist; der dunkle Grund liegt nur
   über der Maske. Darum hängt es per Portal in `#maskenraum` (Schale.tsx)
   und nicht in `body`.

   **Dieselbe Bedienung an jeder Stelle.** `Esc` und ein Klick auf den
   Grund schließen es; ein angefangenes Formular fragt vorher nach. Der
   Fokus geht beim Öffnen hinein, bleibt drin (Tab läuft im Kreis) und
   kehrt beim Schließen dorthin zurück, wo er herkam. Wer den Baustein an
   einer dritten Stelle einhängt, bekommt all das mit — und muss es nicht
   noch einmal bauen.

   Der Inhalt weiß nichts vom Blatt. Was er braucht, ist genau eines:
   ein Weg, das Schließen anzustoßen, der die Nachfrage mitnimmt —
   `useDeckblatt().schliessen`. Der Knopf „Abbrechen" im Fuß eines
   Formulars ruft ihn, nicht den Rückruf des Aufrufers.
   ══════════════════════════════════════════════════════════════════ */

/** Wo das Blatt hineingehängt wird — die `id` des Arbeitsbereichs in der Schale. */
export const DECKBLATT_ZIEL = "maskenraum";

/** Was im Blatt den Fokus tragen kann. */
const FOKUSSIERBAR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), '
  + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Raum = createContext<{ schliessen: () => void } | null>(null);

/* ══════════════ Ein Blatt über dem anderen ══════════════

   **Blätter können sich stapeln, seit dem 17.09.2026.** Der Abschluss der
   Merkliste ist ein Blatt, und darin öffnet die Kundenauswahl „Kunde
   anlegen" — wieder ein Blatt. Beide hängen per Portal nebeneinander im
   Maskenraum, nicht ineinander; ohne diese Liste hörten deshalb **beide**
   auf `Esc`, und die Tastenfalle des unteren zog den Fokus aus dem oberen
   wieder heraus. Wer im Kundenformular die zweite Zeile mit Tab erreichen
   wollte, landete im Blatt darunter.

   Die Liste steht im Modul und nicht in einem Kontext: Ein Kontext trüge
   nur, was UNTER ihm liegt — die Portale liegen daneben. Seit dem
   17.09.2026 ist es dieselbe Liste wie die der Schließanfrage
   (`schale/schliessanfrage.ts`): Deckblatt und Blatt von unten stapeln
   sich gemeinsam, und die Zurückgeste schließt das oberste.

   Dass das obere Blatt auch oben **zeichnet**, entscheidet nicht diese
   Liste, sondern die Reihenfolge im Dokument: Ein später geöffnetes Portal
   hängt hinter dem früheren und liegt damit darüber. */

/** Der Weg heraus, für den Inhalt des Blatts — mit Nachfrage, falls nötig. */
export function useDeckblatt(): { schliessen: () => void } {
  const wert = useContext(Raum);
  if (!wert) throw new Error("useDeckblatt braucht ein Deckblatt darüber.");
  return wert;
}

export function Deckblatt({
  offen, schliessen, titel, untertitel, schmutzig = false, kompakt = false, weite, children,
}: {
  offen: boolean;
  /** Wird gerufen, wenn das Blatt wirklich zugeht — nach der Nachfrage, nicht davor. */
  schliessen: () => void;
  titel: string;
  untertitel?: string;
  /** Steht im Blatt etwas, das verloren ginge? Dann fragt das Schließen nach. */
  schmutzig?: boolean;
  /**
   * Nur so groß wie der Inhalt, in der Mitte — für eine kurze Auskunft.
   *
   * Ein Formular füllt die Fläche, weil es lang ist und rollt. Eine
   * Auskunft, die aus drei Feldern und einer Zahl besteht, sieht in einer
   * Fläche aus, als fehlte etwas (Luca, 16.09.2026).
   */
  kompakt?: boolean;
  /**
   * Die Breite der kompakten Fassung in Pixeln. Ohne Angabe gilt das
   * Kompaktmaß (720 px).
   *
   * Es gibt Auskünfte, die kurz sind und trotzdem breit: Die Preisauskunft
   * trägt einen Pillenkranz über sieben Gerätegruppen, und der brach bei
   * 720 px um (Luca, 16.09.2026: *„Mach die Preisauskunft als Modal etwas
   * breiter, nicht so schmal, sodass die Pills richtig hinpassen."*). Das
   * Kompaktmaß dafür zu verstellen hieße, es für alle zu verstellen — also
   * bekommt die eine Maske ihr eigenes Maß und keine das der anderen.
   */
  weite?: number;
  children: ReactNode;
}) {
  const t = useT();
  const id = useId();
  const blatt = useRef<HTMLDivElement>(null);
  const [fragt, setzeFragt] = useState(false);
  const [ziel, setzeZiel] = useState<HTMLElement | null>(null);

  // Der Arbeitsbereich steht erst, wenn die Schale gezeichnet hat. Ein
  // Zustandswechsel holt das Portal nach, sobald es ihn gibt.
  useEffect(() => { setzeZiel(document.getElementById(DECKBLATT_ZIEL)); }, []);

  const zu = useCallback(() => { setzeFragt(false); schliessen(); }, [schliessen]);

  /* Schließen heißt fragen, wenn etwas drinsteht. Nicht `window.confirm`:
     Das ist ein fremdes Fenster in fremder Sprache, das nicht so aussieht
     wie der Rest — die Nachfrage steht im Blatt selbst. */
  const versuchen = useCallback(() => {
    /* **Erst die Tastatur, dann das Blatt** (20.09.2026) — dieselbe Lücke wie
       im Blatt von unten: Wird ein Deckblatt weggezogen oder daneben
       getippt, fällt das fokussierte Feld stumm aus dem Baum. WebKit meldet
       dabei weder `blur` noch `focusout`, die Tastatur bleibt stehen, und
       was danach gezeichnet wird, liegt dahinter. Gemessen im
       iOS-Simulator: ohne diesen Ruf fuhr sie im ganzen Messfenster
       (2,5 s) nicht ein (`schale/tastatur.ts`). */
    tastaturSchliessen(blatt.current);
    if (schmutzig) setzeFragt(true);
    else zu();
  }, [schmutzig, zu, blatt]);

  // Ist das Formular wieder leer, ist die Frage erledigt.
  useEffect(() => { if (!schmutzig) setzeFragt(false); }, [schmutzig]);

  /* Fokus: hinein beim Öffnen, zurück beim Schließen. Wer mit der Tastatur
     „+ Kunde" gedrückt hat, landet danach wieder auf „+ Kunde" — und nicht
     irgendwo am Anfang der Seite. */
  useEffect(() => {
    if (!offen) return;
    const vorher = document.activeElement as HTMLElement | null;
    const erstes = blatt.current?.querySelector<HTMLElement>(FOKUSSIERBAR);
    (erstes ?? blatt.current)?.focus();
    // Am Finger nie zurück in ein Tippfeld — das holte die Tastatur herauf
    // (19.09.2026, `fokusZurueck` in `Blattvonunten.tsx`).
    return () => { fokusZurueck(vorher); };
  }, [offen]);

  /* **`Esc`, die Zurückgeste auf Android und der Zurück-Knopf** schließen —
     mit derselben Nachfrage wie „Schließen" (`schale/schliessanfrage.ts`,
     40-mobiles-grundgeruest.md Schritt 7). Die Taste hört dort in der
     Fangphase, damit das Blatt vor dem Zeitraumwähler und der Seitenleiste
     dran ist — ein Blatt, das offen bleibt, während darunter der Kalender
     zugeht, wäre die falsche Antwort. */
  const istOben = useSchliessanfrage(offen, versuchen);

  /* Tab bleibt im Blatt. */
  useEffect(() => {
    if (!offen) return;
    const taste = (e: KeyboardEvent) => {
      // Liegt ein Blatt darüber, gehört ihm die Taste.
      if (!istOben()) return;
      if (e.key !== "Tab" || !blatt.current) return;
      const alle = Array.from(blatt.current.querySelectorAll<HTMLElement>(FOKUSSIERBAR))
        .filter((el) => el.offsetParent !== null);
      const erstes = alle[0];
      const letztes = alle[alle.length - 1];
      if (!erstes || !letztes) return;
      if (e.shiftKey && document.activeElement === erstes) { e.preventDefault(); letztes.focus(); }
      else if (!e.shiftKey && document.activeElement === letztes) { e.preventDefault(); erstes.focus(); }
    };
    document.addEventListener("keydown", taste, true);
    return () => document.removeEventListener("keydown", taste, true);
  }, [offen, istOben]);

  /* ── Am Telefon: Vollbild oder von unten ──
     Die Regel steht in `Blattvonunten.tsx` („Die Regel für Blätter am
     Telefon"): Ein Formular und eine lange Fläche füllen den Schirm; eine
     kurze Auskunft kommt als Blatt von unten, mit Griff, und lässt sich
     wegziehen. `kompakt` ist die Auskunft, die Fläche ist alles andere —
     und eine Auskunft mit einem Feld zum Tippen ist ein Formular. Die
     Form steht als `data-form` am Schirm; der Baum darunter bleibt
     derselbe, damit ein Blatt, das nach dem Laden zum Formular wird, nichts
     verliert, was darin schon getippt ist. */
  const telefon = useTelefon();
  const schirm = useRef<HTMLDivElement>(null);
  const leib = useRef<HTMLDivElement>(null);
  const form = useBlattform(offen, blatt, kompakt ? "unten" : "vollbild", ziel);
  const unten = telefon && form === "unten";
  const griffe = useZiehen({ blatt, schirm, leib, zu: versuchen });

  if (!offen || !ziel) return null;

  return createPortal(
    <div
      className={`deck-schirm${kompakt ? " kompakt" : ""}`}
      data-form={form}
      ref={schirm}
      // Die Breite geht als Variable hinein, nicht als zweite Klasse: Ein
      // Maß ist eine Zahl und keine Sorte. `min(…, 100%)` steht weiter in
      // der CSS-Regel — am schmalen Schirm gewinnt immer der Schirm.
      {...(weite ? { style: { "--deck-weite": `${weite}px` } as CSSProperties } : {})}
      // Nur der Grund selbst schließt — nicht ein Klick, der im Blatt
      // beginnt und über den Rand hinausrutscht.
      onMouseDown={(e) => { if (e.target === e.currentTarget) versuchen(); }}
    >
      <div
        className="deck-blatt"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-titel`}
        ref={blatt}
        tabIndex={-1}
        {...(unten ? {
          onPointerDown: griffe.anfassen,
          onPointerMove: griffe.ziehen,
          onPointerUp: griffe.loslassen,
          onPointerCancel: griffe.loslassen,
        } : {})}
      >
        {/* Der Griff des Blattes von unten — derselbe wie dort, und ein
            Tipper darauf schließt. Im Vollbild und am Schreibtisch steht
            stattdessen „Schließen" im Kopf. */}
        {unten ? (
          <button
            type="button"
            className="bvu-griff"
            onClick={versuchen}
            aria-label={t("allgemein.schliessen")}
            title={t("allgemein.schliessen")}
          >
            <span className="bvu-griff-balken" aria-hidden="true" />
          </button>
        ) : null}
        <header className="deck-kopf">
          <div className="deck-kopf-text">
            <h3 id={`${id}-titel`}>{titel}</h3>
            {untertitel ? <p>{untertitel}</p> : null}
          </div>
          {unten ? null : (
            <Knopf klein art="stumm" onClick={versuchen}>{t("allgemein.schliessen")}</Knopf>
          )}
        </header>

        {fragt ? (
          <div className="deck-frage">
            <Meldung
              art="acht"
              titel="deckblatt.verwerfenFrage"
              knoepfe={(
                <>
                  <Knopf klein art="haupt" onClick={() => setzeFragt(false)}>
                    {t("deckblatt.weiterBearbeiten")}
                  </Knopf>
                  <Knopf klein onClick={zu}>{t("deckblatt.verwerfen")}</Knopf>
                </>
              )}
            >
              <T k="deckblatt.verwerfenSatz" />
            </Meldung>
          </div>
        ) : null}

        <Raum.Provider value={{ schliessen: versuchen }}>
          <div className="deck-leib" ref={leib}>{children}</div>
        </Raum.Provider>
      </div>
    </div>,
    ziel,
  );
}
